import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AlertTriangle, Plug, RefreshCw, Unplug } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import type {
  TelegramChatSummary,
  TelegramDirectLink,
  TelegramOneClickAvailability,
} from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import {
  TELEGRAM_CHAT_STATE_PENDING,
  isTelegramGroupChat,
} from '@/Domain/components/Integration/Telegram/TelegramChatRow';
import {
  TelegramOneClickSection,
  hasOneClickSurface,
} from '@/Domain/components/Integration/Telegram/TelegramOneClickSection';
import { IntegrationConnectDialog } from '@/Domain/components/Integration/components/IntegrationConnectDialog';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationSetupOption } from '@/Domain/components/Integration/components/IntegrationSetupOption';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { TelegramIcon } from '@/Domain/components/icons/Telegram.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';

const TELEGRAM_INTEGRATION_NAME = 'telegram' as const;
const TELEGRAM_QUERY_KEY = [QueryKey.integration.get, TELEGRAM_INTEGRATION_NAME] as const;
const TELEGRAM_DOCS_URL = 'https://docs.quickchat.ai/channels/telegram';
const BOTFATHER_URL = 'https://t.me/botfather';
const LINK_CLASS =
  'text-foreground link-underline underline-offset-2 transition-colors hover:text-muted-foreground';

interface TelegramIntegrationParams {
  telegram_bot_active: boolean;
  telegram_bot_token: string;
}

interface TelegramIntegrationResponse {
  name: typeof TELEGRAM_INTEGRATION_NAME;
  params: TelegramIntegrationParams;
}

const telegramFormSchema = z.object({
  telegram_bot_active: z.boolean(),
  telegram_bot_token: z.string(),
});

type TelegramFormValues = z.infer<typeof telegramFormSchema>;

interface TelegramSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The own-bot half of the sheet. Named as the advanced alternative only when the
 * shared-bot option is on screen; on its own it is just the sheet. */
const OwnBotSetup = ({ named, children }: { named: boolean; children: ReactNode }) => {
  const { t } = useTranslation(['integration']);
  if (!named) return <>{children}</>;
  return (
    <IntegrationSetupOption
      title={t('integration:telegram.setup.own.title')}
      badge={t('integration:telegram.setup.own.badge')}
      description={t('integration:telegram.setup.own.description')}
      data-testid="telegram-own-bot-setup"
    >
      {children}
    </IntegrationSetupOption>
  );
};

export const TelegramSheet = ({ open, onOpenChange }: TelegramSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);

  const { data, isSuccess } = useQuery<TelegramIntegrationResponse>({
    queryKey: TELEGRAM_QUERY_KEY,
    enabled: open,
  });

  const { data: availability } = useQuery<TelegramOneClickAvailability>({
    queryKey: [QueryKey.integration.telegramOneClickAvailability],
    enabled: open,
  });
  const oneClickAvailable = availability?.available === true;
  const shouldReadDirectLink =
    oneClickAvailable ||
    availability?.direct_link_active === true ||
    availability?.direct_link_active === null;
  const botFatherPrerequisites = (
    t('integration:telegram.prerequisites', { returnObjects: true }) as string[]
  ).map((_, index) => (
    <Trans
      key={index}
      t={t}
      i18nKey={`integration:telegram.prerequisites.${index}`}
      components={[
        <a
          key="botfather"
          href={BOTFATHER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={LINK_CLASS}
        />,
      ]}
    />
  ));
  // Only worth naming the own-bot form as "the advanced one" when there is another
  // option on screen to be advanced relative to.
  const showsSetupOptions = hasOneClickSurface(availability);

  const params = data?.params;

  // Fetch the bot profile live so Telegram-side renames are reflected.
  const { data: botInfo, isFetching: botInfoLoading } = useQuery<{
    name: string;
    username: string;
  }>({
    queryKey: [QueryKey.integration.telegramBotInfo],
    enabled: open && Boolean(params?.telegram_bot_token),
  });

  // Same query keys the one-click section uses, so this costs a cache read, not a request.
  const { data: sharedChats } = useQuery<TelegramChatSummary[]>({
    queryKey: [QueryKey.integration.telegramChats],
    enabled: open,
  });
  const {
    data: directLink,
    isFetching: directLinkFetching,
    isError: directLinkReadFailed,
  } = useQuery<TelegramDirectLink>({
    queryKey: [QueryKey.integration.telegramDirectLink],
    enabled: open && shouldReadDirectLink,
  });

  // Clearing the prefilled saved token disconnects the own-bot integration.
  const initialValues = useMemo<TelegramFormValues>(
    () => ({
      telegram_bot_active: params?.telegram_bot_active ?? false,
      telegram_bot_token: params?.telegram_bot_token ?? '',
    }),
    [params],
  );

  const form = useForm<TelegramFormValues>({
    resolver: zodResolver(telegramFormSchema),
    defaultValues: initialValues,
    values: initialValues,
  });

  useEffect(() => {
    if (open) {
      capture(PostHogEventEnum.TelegramIntegrationSheetOpened);
    }
  }, [open, capture]);

  // Own-bot Connected means authenticated; Enabled means its webhook is active.
  const isConnected = Boolean(params?.telegram_bot_token);
  const isEnabled = Boolean(params?.telegram_bot_active);
  const activeSharedChats = (sharedChats ?? []).filter(
    (chat) => isTelegramGroupChat(chat) && chat.state !== TELEGRAM_CHAT_STATE_PENDING,
  );
  const directLinkActive =
    shouldReadDirectLink &&
    (directLinkFetching || directLinkReadFailed || directLink === undefined
      ? availability?.direct_link_active === true
      : directLink.active);
  // Header badges combine the own-bot and shared-bot setups.
  const channelConnected = isConnected || activeSharedChats.length > 0 || directLinkActive;
  const channelEnabled =
    isEnabled || activeSharedChats.some((chat) => chat.enabled) || directLinkActive;
  // The token is `<bot_id>:<secret>`; the bot id prefix is public, the rest secret.
  const botId = (params?.telegram_bot_token ?? '').split(':')[0];
  const botName = botInfo?.name ?? '';
  const botUsername = botInfo?.username ?? '';

  const mutation = useMutation<
    TelegramIntegrationResponse,
    AxiosError<{ detail?: string }>,
    { name: typeof TELEGRAM_INTEGRATION_NAME; params: Partial<TelegramIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response) => {
      queryClient.setQueryData(TELEGRAM_QUERY_KEY, (prev?: TelegramIntegrationResponse) => ({
        name: TELEGRAM_INTEGRATION_NAME,
        params: {
          ...(prev?.params ?? { telegram_bot_active: false, telegram_bot_token: '' }),
          ...response.params,
        },
      }));
      toast.success(t('integration:telegram.saved'));
    },
    onError: (error) => {
      // Surface the backend `detail` when present; never log the token itself.
      const detail = error.response?.data?.detail;
      capture(PostHogEventEnum.TelegramIntegrationSaveFailed, { reason: detail ?? 'unknown' });
      toast.error(detail ?? t('integration:telegram.save_failed'));
    },
  });

  // The toggle has standardized feedback and does not close the sheet.
  const toggleMutation = useMutation<
    TelegramIntegrationResponse,
    AxiosError<{ detail?: string }>,
    { name: typeof TELEGRAM_INTEGRATION_NAME; params: Partial<TelegramIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response, variables) => {
      queryClient.setQueryData(TELEGRAM_QUERY_KEY, (prev?: TelegramIntegrationResponse) => ({
        name: TELEGRAM_INTEGRATION_NAME,
        params: {
          ...(prev?.params ?? { telegram_bot_active: false, telegram_bot_token: '' }),
          ...response.params,
        },
      }));
      const name = t('integration:telegram.title');
      toast.success(
        variables.params.telegram_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      toast.error(detail ?? t('integration:telegram.save_failed'));
    },
  });

  const tokenValue = form.watch('telegram_bot_token');

  // Connecting saves the token and activates the own bot.
  const handleConnect = form.handleSubmit((values) => {
    const trimmedToken = values.telegram_bot_token.trim();
    if (!trimmedToken) return;
    trackIntegrationConnectStarted(capture, 'telegram');
    mutation.mutate(
      {
        name: TELEGRAM_INTEGRATION_NAME,
        params: { telegram_bot_token: trimmedToken, telegram_bot_active: true },
      },
      {
        onSuccess: () => {
          setConnectDialogOpen(false);
          capture(PostHogEventEnum.TelegramIntegrationSaved, {
            bot_active: true,
            token_changed: trimmedToken !== (params?.telegram_bot_token ?? ''),
            token_removed: false,
          });
          trackIntegrationConnectSucceeded(capture, 'telegram');
        },
        onError: () => {
          // Raw backend detail stays on the Telegram-specific failure event.
          trackIntegrationConnectFailed(capture, 'telegram', 'save_failed');
        },
      },
    );
  });

  // Disconnect = remove the credential (de-authenticate); also turns the bot off.
  const handleDisconnect = () => {
    mutation.mutate({
      name: TELEGRAM_INTEGRATION_NAME,
      params: { telegram_bot_token: '', telegram_bot_active: false },
    });
  };

  // Enable/disable toggle = pause/resume the webhook without losing the token.
  const handleToggleActive = (checked: boolean) => {
    toggleMutation.mutate({
      name: TELEGRAM_INTEGRATION_NAME,
      params: { telegram_bot_active: checked },
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0 space-y-0"
        data-testid="telegram-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <TelegramIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="telegram-sheet-title">
                  {t('integration:telegram.title')}
                </SheetTitle>
                <IntegrationStatusBadge connected={channelConnected} />
                <IntegrationEnabledBadge enabled={channelEnabled} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="telegram-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          <IntegrationIntroduction
            description={
              showsSetupOptions
                ? `${t('integration:telegram.overview')} ${t('integration:telegram.setup.two_ways')}`
                : t('integration:telegram.overview')
            }
            prerequisites={showsSetupOptions ? undefined : botFatherPrerequisites}
            docsHref={TELEGRAM_DOCS_URL}
          />

          <TelegramOneClickSection />

          {!isSuccess ? (
            <div className="flex items-center justify-center py-12" data-testid="telegram-loading">
              <Spinner className="size-5 text-muted-foreground" />
            </div>
          ) : (
            <OwnBotSetup named={showsSetupOptions}>
              {showsSetupOptions ? (
                <div className="space-y-1.5" data-testid="telegram-own-bot-prerequisites">
                  <p className="m-0 font-medium text-foreground text-sm">
                    {t('integrations:section.prerequisites')}
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm marker:text-foreground-subtle">
                    {botFatherPrerequisites.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <IntegrationSection
                title={t('integrations:section.connect')}
                titleClassName={showsSetupOptions ? 'text-sm' : undefined}
                data-testid="telegram-connect-enable-section"
              >
                {isConnected ? (
                  <IntegrationConnectionCard
                    data-testid="telegram-connection-card"
                    details={[
                      {
                        label: t('integration:telegram.bot_name'),
                        value: botInfoLoading ? (
                          <Spinner
                            className="size-3.5 text-foreground-subtle"
                            data-testid="telegram-bot-name-loader"
                          />
                        ) : (
                          botName
                        ),
                        'data-testid': 'telegram-bot-name',
                      },
                      {
                        label: t('integration:telegram.bot_username'),
                        value: botInfoLoading ? (
                          <Spinner
                            className="size-3.5 text-foreground-subtle"
                            data-testid="telegram-bot-username-loader"
                          />
                        ) : botUsername ? (
                          `@${botUsername}`
                        ) : (
                          ''
                        ),
                        'data-testid': 'telegram-bot-username',
                      },
                      {
                        label: t('integration:telegram.bot_id'),
                        value: botId,
                        'data-testid': 'telegram-bot-id',
                      },
                    ]}
                    actions={
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={() => setConnectDialogOpen(true)}
                          data-testid="telegram-reconnect-button"
                        >
                          <RefreshCw className="size-4" />
                          {t('integrations:reconnect')}
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="flex-1"
                          onClick={handleDisconnect}
                          disabled={mutation.isPending}
                          data-testid="telegram-disconnect-button"
                        >
                          <Unplug className="size-4" />
                          {t('integrations:disconnect')}
                        </Button>
                      </>
                    }
                  />
                ) : (
                  <Button
                    type="button"
                    variant="dark"
                    size="sm"
                    className="w-full"
                    onClick={() => setConnectDialogOpen(true)}
                    data-testid="telegram-connect-button"
                  >
                    <Plug className="size-4" />
                    {t('integrations:connect', { name: 'Telegram' })}
                  </Button>
                )}
              </IntegrationSection>

              <IntegrationSection
                title={t('integrations:section.settings')}
                titleClassName={showsSetupOptions ? 'text-sm' : undefined}
                data-testid="telegram-settings-section"
              >
                <IntegrationToggleField
                  id="telegram-enable-switch"
                  // telegram_bot_active gates the own bot only: get_telegram_implementation
                  // filters on it, and no canonical path reads it. Calling that "Enable
                  // Telegram" next to a live shared bot claims a scope it does not have.
                  label={
                    showsSetupOptions
                      ? t('integration:telegram.setup.own.enable')
                      : t('integrations:enable', { name: 'Telegram' })
                  }
                  checked={isEnabled}
                  onCheckedChange={handleToggleActive}
                  disabled={!isConnected || toggleMutation.isPending}
                  data-testid="telegram-enable-switch"
                />
                {isEnabled && activeSharedChats.some((chat) => chat.enabled) && (
                  <Alert variant="warning" data-testid="telegram-own-bot-overlap-note">
                    <AlertTriangle className="size-4" />
                    <AlertDescription>
                      {t('integration:telegram.oneclick.own_bot_note')}
                    </AlertDescription>
                  </Alert>
                )}
              </IntegrationSection>
            </OwnBotSetup>
          )}
        </div>

        <IntegrationConnectDialog
          open={connectDialogOpen}
          onOpenChange={setConnectDialogOpen}
          title={t('integrations:connect', { name: 'Telegram' })}
          description={
            <Trans
              t={t}
              i18nKey="integration:telegram.connect_description"
              components={[
                <a
                  key="botfather"
                  href={BOTFATHER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK_CLASS}
                />,
              ]}
            />
          }
          submitLabel={t('integrations:connect', { name: 'Telegram' })}
          submitIcon={<Plug className="size-4" />}
          onSubmit={handleConnect}
          submitDisabled={!tokenValue?.trim() || mutation.isPending}
          isSubmitting={mutation.isPending}
          data-testid="telegram-connect-dialog"
        >
          <Controller
            control={form.control}
            name="telegram_bot_token"
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor="telegram-bot-token-input">
                  {t('integration:telegram.bot_token.label')}
                </Label>
                <Input
                  id="telegram-bot-token-input"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder={t('integration:telegram.bot_token.placeholder')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  data-testid="telegram-bot-token-input"
                  className="w-full"
                />
              </div>
            )}
          />
        </IntegrationConnectDialog>
      </SheetContent>
    </Sheet>
  );
};
